import {Match, MatchMessage, MatchNewDTO, ReactionDTO, User} from '@/domain';
import {matchStore} from '@/entities/match';
import {notificationStore} from '@/entities/notification';

import {WorkerActions} from '../workerActions';

const handledActions = [
  WorkerActions.SocketMessage,
  WorkerActions.UpdateChat,
  WorkerActions.GetData,
  WorkerActions.UpdateChatsList,
  WorkerActions.UpdateMatches,
];

enum SocketMessageTypes {
  MatchMessage = 'match.message',
  MatchCompliment = 'match.compliment',
  Interaction = 'match.interaction',
}

export const handleMatchesEvent = (event: MessageEvent) => {
  const {action, data, dataKey} = event.data;

  if (!handledActions.includes(action)) {
    return;
  }

  if (action === WorkerActions.SocketMessage) {
    switch (data.res) {
      case SocketMessageTypes.Interaction:
        if (data.action === 'started') {
          matchStore.setIsUserInteracting(true, data.model.match_id);
        } else if (data.action === 'stopped') {
          matchStore.setIsUserInteracting(false, data.model.match_id);
        }
        break;
      case SocketMessageTypes.MatchMessage:
        if (data.action === 'received') {
          const {match_id, sender_user_id} = data.model as MatchMessage;
          if (match_id && matchStore.selectedChat?.id !== match_id) {
            const user = matchStore.matches.users.find(
              (findUser) => findUser.id === sender_user_id
            );
            notificationStore.addChatMessageNotification({
              matchId: match_id,
              user,
            });
          }
        }
        break;
      case SocketMessageTypes.MatchCompliment:
        {
          if (data.action !== 'received') {
            return;
          }

          const {match, user}: {match: Match; user: User} = data.model;
          matchStore.addMatch({match, user});
          notificationStore.addComplimentNotification({match, user});
        }
        break;
      case 'match':
        if (
          data.action === 'received' &&
          data.model.event_type === 'new_match'
        ) {
          const {user, match} = data.model as MatchNewDTO;
          matchStore.addMatch({match, user});
          notificationStore.addBumpNotification({user});
        } else if (data.action === 'deleted') {
          const match = matchStore.matches.list_new.find(
            (match: Match) => match.id === data.model.match_id
          );
          if (match) {
            matchStore.setMatches({
              ...matchStore.matches,
              list_new: matchStore.matches.list_new.filter(
                (m: Match) => m.id !== match.id
              ),
              list_open: [match, ...matchStore.matches.list_open],
            });
          }
        }
        break;
      case 'visits':
        if (
          data.action === 'created' &&
          (data.model.event_type === 'new_visit.invisible' ||
            data.model.event_type === 'new_visit.visible')
        ) {
          const {user, visitor, visit} = data.model;
          matchStore.setVisits({
            list: [visit, ...matchStore.liked_and_viewed.viewed.list],
            users: user
              ? [user, ...matchStore.liked_and_viewed.viewed.users]
              : matchStore.liked_and_viewed.viewed.users,
            visitors: visitor
              ? [visitor, ...matchStore.liked_and_viewed.viewed.visitors]
              : matchStore.liked_and_viewed.viewed.visitors,
            total_count: matchStore.liked_and_viewed.viewed.users.length + 1,
          });
          notificationStore.addVisitNotification({
            visit,
            user,
            visitor,
          });
        }
        break;
      case 'reaction':
        if (data.action === 'received') {
          const {reaction, user} = data.model as ReactionDTO;
          if (reaction.type === 'smash') {
            if (user) {
              notificationStore.addSmashUserVisibleNotification({
                reaction,
                user,
              });
            } else if (reaction.user) {
              notificationStore.addSmashUserInvisibleNotification({
                reaction,
                hiddenUser: reaction.user,
              });
            }
          }

          if (reaction.type === 'super_smash' && user) {
            notificationStore.addSuperSmashNotification({
              reaction,
              user,
            });
          }

          matchStore.setLikes({
            list: [reaction, ...matchStore.liked_and_viewed.liked.list],
            users: user
              ? [user, ...matchStore.liked_and_viewed.liked.users]
              : matchStore.liked_and_viewed.liked.users,
            total_count: matchStore.liked_and_viewed.liked.total_count,
          });
        }
        break;
      case 'reaction.total_count':
        matchStore.setLikes({
          list: [...matchStore.liked_and_viewed.liked.list],
          users: [...matchStore.liked_and_viewed.liked.users],
          total_count: data.model.value,
        });
        break;
    }
  } else if (action === WorkerActions.UpdateChat) {
    matchStore.setNewChat(data);
  } else if (action === WorkerActions.UpdateMatches) {
    matchStore.setMatches({
      ...matchStore.matches,
      list_new: [data.match, ...matchStore.matches.list_new],
      users: [data.user, ...matchStore.matches.users],
    });
  } else if (action === WorkerActions.GetData) {
    switch (dataKey) {
      case 'matches':
        matchStore.setMatches(data);
        break;
    }
  } else if (action === WorkerActions.UpdateChatsList) {
    matchStore.setMatches(data);
  }
};
